import { Controller } from "@hotwired/stimulus"
import Brainfish from "@brainfish-ai/web-widget"

const CHAT_COOKIE_NAME = "zendesk_chat_active"
const CHAT_COOKIE_TTL_SECONDS = 1200 // 20 min — keep Zendesk available after chat ends
const CHAT_POLL_INTERVAL_MS = 5000

export default class BrainfishController extends Controller {
  static connectionCount = 0
  static isWidgetOpen = false

  // Handlers defined here so that any instance of the controller
  // can clean them up.
  static handleOpen = null
  static handleClose = null
  static handleBeforeUnload = null
  static pollInterval = null

  // !NOTE: This controller can be bound to multiple elements on the page
  // (e.g. header and footer elements). We only want to initialize
  // the Brainfish widget once. The first connect sets up listeners
  // and polling; the last disconnect tears them down.
  connect() {
    BrainfishController.connectionCount++
    if (BrainfishController.connectionCount > 1) return

    BrainfishController.handleOpen = () => { BrainfishController.isWidgetOpen = true }
    BrainfishController.handleClose = () => { BrainfishController.isWidgetOpen = false }
    BrainfishController.handleBeforeUnload = () => {
      if (!BrainfishController.isChatting()) {
        BrainfishController.clearChatCookie()
      }
    }

    window.addEventListener("onBrainfishWidgetOpen", BrainfishController.handleOpen)
    window.addEventListener("onBrainfishWidgetClose", BrainfishController.handleClose)
    window.addEventListener("beforeunload", BrainfishController.handleBeforeUnload)

    const widgetKey = document.querySelector('meta[name="brainfish_widget_key"]')?.content

    // Skip initialization of Brainfish if widget key is not present as we do not
    // provide the key for all environments.
    if (widgetKey) {
      try {
        Brainfish.Widgets.init({
          widgetKey,
          overrides: {
            hideTriggerButton: true,
          }
        })
      } catch (e) {
        console.error("[Brainfish] Widget init failed:", e)
      }
    }

    BrainfishController.pollInterval = setInterval(() => BrainfishController.pollChatStatus(), CHAT_POLL_INTERVAL_MS)
  }

  disconnect() {
    BrainfishController.connectionCount--
    if (BrainfishController.connectionCount > 0) return

    window.removeEventListener("onBrainfishWidgetOpen", BrainfishController.handleOpen)
    window.removeEventListener("onBrainfishWidgetClose", BrainfishController.handleClose)
    window.removeEventListener("beforeunload", BrainfishController.handleBeforeUnload)

    BrainfishController.isWidgetOpen = false

    if (BrainfishController.pollInterval) {
      clearInterval(BrainfishController.pollInterval)
      BrainfishController.pollInterval = null
    }
  }

  open() {
    if (BrainfishController.chatActive()) {
      if (typeof zE !== "undefined") {
        zE("webWidget", "open")
      }
    } else {
      if (BrainfishController.isWidgetOpen) {
        Brainfish.Widgets.close()
      } else {
        Brainfish.Widgets.open({ position: "bottom-left" })
      }
    }
  }

  // private

  static chatActive() {
    return BrainfishController.hasChatCookie() || BrainfishController.isChatting()
  }

  static setChatCookie(ttlSeconds) {
    const expires = new Date(Date.now() + ttlSeconds * 1000).toUTCString()
    document.cookie = `${CHAT_COOKIE_NAME}=true; path=/; expires=${expires}; SameSite=Lax; Secure`
  }

  static clearChatCookie() {
    document.cookie = `${CHAT_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`
  }

  static hasChatCookie() {
    return document.cookie.split("; ").some(c => c.startsWith(`${CHAT_COOKIE_NAME}=`))
  }

  static isChatting() {
    try {
      return typeof zE !== "undefined" && zE("webWidget:get", "chat:isChatting")
    } catch {
      return false
    }
  }

  static pollChatStatus() {
    if (BrainfishController.isChatting()) {
      BrainfishController.setChatCookie(CHAT_COOKIE_TTL_SECONDS)
    }
  }
}
